import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Add CSS for blob animation
const blobAnimation = `
  @keyframes blob {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }
  .animate-blob {
    animation: blob 7s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  @keyframes soft-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.85; }
  }
  .animate-soft-pulse {
    animation: soft-pulse 2.5s ease-in-out infinite;
  }
  .homepage-root .home-action-icon {
    font-size: 3.5rem !important;
    line-height: 1 !important;
    width: 4.25rem !important;
    height: 4.25rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-bottom: 0 !important;
  }
  .homepage-root .home-action-label {
    font-size: 15px !important;
    line-height: 1.25 !important;
    font-weight: 700 !important;
  }
  .homepage-root button.home-action-btn {
    min-height: 132px !important;
    padding: 14px 10px !important;
    margin-bottom: 0 !important;
    width: 100% !important;
  }
  @media (min-width: 640px) {
    .homepage-root .home-action-icon {
      font-size: 2.75rem !important;
      width: 3.5rem !important;
      height: 3.5rem !important;
    }
    .homepage-root button.home-action-btn {
      min-height: 96px !important;
    }
  }
`;

const HomePage = ({ user }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const quickActions = [
    { icon: '🍀', label: 'Szczęśliwe liczby', short: 'Szczęśliwe', path: '/my-lucky-numbers', accent: 'from-emerald-500/90 to-teal-600/90' },
    { icon: '🚀', label: 'Rozpocznij losowanie', short: 'Losowanie', path: '/generator', accent: 'from-amber-500/90 to-orange-600/90' },
    { icon: '🎰', label: 'Gry i zabawy', short: 'Gry', path: '/gry', accent: 'from-fuchsia-500/90 to-pink-600/90' },
    { icon: '🧮', label: 'Systemy skrócone', short: 'Systemy', path: '/systems', accent: 'from-violet-500/90 to-purple-600/90' },
    { icon: '🤖', label: 'AI Ultra Pro', short: 'AI Pro', path: '/ai-ultra-pro', accent: 'from-blue-500/90 to-indigo-600/90' },
    { icon: '💬', label: 'Chat społeczności', short: 'Chat', path: '/chat', accent: 'from-orange-500/90 to-amber-600/90' },
  ];

  useEffect(() => {
    setIsVisible(true);
  }, [user]);

  const handleStartDrawing = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => navigate('/generator'), 400);
  };

  const WaveHand = () => (
    <motion.span
      className="inline-block text-3xl sm:text-4xl md:text-5xl text-amber-400"
      animate={{ rotate: [0, 20, -15, 20, 0] }}
      transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5, ease: 'easeInOut' }}
    >
      👋
    </motion.span>
  );

  return (
    <>
      <style>{blobAnimation}</style>
      <div className="min-h-screen bg-[#0f172a] flex justify-center relative overflow-hidden homepage-root">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.18),transparent_50%),radial-gradient(ellipse_at_bottom_right,_rgba(245,158,11,0.12),transparent_45%)]" />
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 -left-4 w-56 h-56 bg-indigo-500 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute top-20 -right-4 w-56 h-56 bg-amber-500 rounded-full mix-blend-screen filter blur-3xl opacity-15 animate-blob animation-delay-2000" />
      </div>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="w-full max-w-4xl px-3 sm:px-6 py-4 sm:py-8"
          >
            <motion.button
              type="button"
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              onClick={() => navigate('/chat')}
              className="w-full max-w-2xl mx-auto mb-5 sm:mb-6 flex items-center gap-3 rounded-2xl border border-orange-400/45 bg-gradient-to-r from-orange-500/25 via-amber-500/15 to-orange-500/10 backdrop-blur-md p-3.5 sm:p-4 text-left shadow-lg shadow-orange-900/20 animate-soft-pulse hover:border-orange-400/70 transition-colors"
            >
              <span className="text-3xl shrink-0">💬</span>
              <div className="flex-1 min-w-0">
                <p className="text-amber-400 text-[11px] sm:text-xs font-bold uppercase tracking-widest mb-0.5">Nowość</p>
                <p className="text-white text-sm sm:text-base font-semibold leading-snug">
                  Chat generatora — podziel się doświadczeniami z innymi graczami!
                </p>
              </div>
              <span className="shrink-0 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-full">
                NEW
              </span>
            </motion.button>

            <motion.section
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center py-6 sm:py-10 md:py-14 w-full"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-4 sm:mb-6"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <WaveHand />
                  <h2 className="text-lg sm:text-2xl md:text-3xl font-semibold text-white/95">
                    Witaj, {user?.displayName || user?.email?.split('@')[0] || 'Użytkowniku'}!
                  </h2>
                </div>
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight px-2"
              >
                Inteligentny Generator Lotto
                <span className="block text-amber-400 text-base sm:text-xl md:text-2xl font-medium mt-1">AI + matematyka + statystyki</span>
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base sm:text-base md:text-lg text-slate-200 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-2"
              >
                Losuj liczby nowocześnie — z wykorzystaniem AI, praw matematycznych i analizy statystycznej.
              </motion.p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-3 md:gap-4 w-full max-w-2xl mx-auto px-0.5">
                {quickActions.map((action, index) => (
                  <motion.button
                    key={action.path}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.45 + index * 0.06 }}
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => (action.path === '/generator' ? handleStartDrawing() : navigate(action.path))}
                    className="home-action-btn group relative overflow-hidden rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md text-center transition-shadow hover:shadow-lg hover:shadow-amber-500/15 active:border-white/30"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${action.accent} opacity-0 group-hover:opacity-100 group-active:opacity-90 transition-opacity duration-300`} />
                    <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2 sm:gap-1.5">
                      <span className="home-action-icon" aria-hidden>
                        {action.icon}
                      </span>
                      <span className="home-action-label text-white leading-tight tracking-wide drop-shadow-sm px-0.5">
                        <span className="sm:hidden">{action.short}</span>
                        <span className="hidden sm:inline">{action.label}</span>
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.section>

            {/* Sekcja informacyjna */}
            <motion.section
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="max-w-3xl mx-auto mb-8 sm:mb-12"
            >
              <motion.div
                className="relative overflow-visible rounded-[1.75rem] sm:rounded-3xl p-5 sm:p-7 bg-gradient-to-br from-white/15 via-white/10 to-emerald-500/10 backdrop-blur-xl border border-white/25 shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
              >
                <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent" />
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-5 text-center drop-shadow-sm">
                  ✨ Dlaczego warto?
                </h3>
                <div className="space-y-3 sm:space-y-4 max-w-2xl mx-auto">
                  {[
                    { icon: '🧠', text: 'Nasz system opiera się na prawach matematycznych oraz strategiach tworzonych przez sztuczną inteligencję.' },
                    { icon: '💰', text: 'Celem jest ograniczanie budżetu i wsparcie odpowiedzialnej gry.' },
                    { icon: '🎲', text: 'Żaden system nie gwarantuje wygranej – traktuj to jako zabawę i inspirację.' },
                  ].map((item) => (
                    <div
                      key={item.icon}
                      className="flex items-start gap-3 p-4 rounded-2xl bg-white/8 border border-white/15 backdrop-blur-sm"
                    >
                      <span className="text-3xl sm:text-2xl flex-shrink-0 leading-none">{item.icon}</span>
                      <p className="text-[15px] sm:text-base text-slate-100 leading-relaxed text-left font-medium">{item.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.section>

            {/* Call to action na dole */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              className="text-center pb-6 sm:pb-8"
            >
              <div
                className="relative overflow-hidden rounded-2xl p-5 sm:p-6 border border-amber-400/35 shadow-xl shadow-amber-900/25 max-w-2xl mx-auto bg-gradient-to-br from-amber-500/15 via-white/10 to-white/5 backdrop-blur-md"
              >
                <p className="text-base sm:text-lg font-bold text-white mb-1 drop-shadow-sm">
                  🚀 Skorzystaj z generatorów już teraz
                </p>
                <p className="text-[15px] sm:text-base text-slate-200 leading-relaxed">
                  Użyj szybkich skrótów powyżej lub menu u góry strony.
                </p>
              </div>
            </motion.section>

            {/* Dekoracyjne elementy tła */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
              <motion.div
                animate={{
                  x: [0, 100, 0],
                  y: [0, -50, 0],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"
              />
              <motion.div
                animate={{
                  x: [0, -80, 0],
                  y: [0, 60, 0],
                }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"
              />
              <motion.div
                animate={{
                  x: [0, 60, 0],
                  y: [0, -80, 0],
                }}
                transition={{
                  duration: 30,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute top-1/2 left-1/4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl"
              />
              
              {/* Dodatkowe elementy dekoracyjne */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute top-1/3 right-1/4 w-16 h-16 bg-yellow-400/20 rounded-full blur-lg"
              />
              
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 40,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute bottom-1/3 left-1/3 w-20 h-20 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-lg"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
};

export default HomePage;
